import React, { useEffect, useState } from "react";
import { ExpandMore, ExpandLess } from '@mui/icons-material';

import {
    FormFeedback,
    Input,
    Label,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem
} from 'reactstrap';
import { getAllDepartmentManagers, getUserOnbehalfReportToManager, getUserReportToManager } from "../../../shared/services/drivingExemptionApi";
import { UserSupervisor } from "../../../shared/models/api/models";
import { toISOLocalDate } from "../../../shared/models/mapping/mapping";
import { usingRPAutoPopulatedReportToEmail } from "../drivingExemption/DrivingExemptionFormState";
import Form from 'react-bootstrap/Form'
import { getHazards, getLocations, getRisks } from "../../../shared/services/railOperationalFormsApi";
import {
    TakeFiveFormState,
    takeFiveFormState as t
} from "../takeFive/TakeFiveFormState";
import TakeFiveQuestions from "../takeFive/TakeFiveQuestions";

interface OptionValueLabel {
    value: number;
    label: string;
    color?: string;
}

interface AnswerProps {
    onAnswerUpdate: (value: any) => any;
    invalid?: boolean;
    optional?: boolean;
}

interface SelectAnswerProps extends AnswerProps {
    options: OptionValueLabel[];
    selectedAnswer?: OptionValueLabel;
}

export const DropdownSelect = (props: SelectAnswerProps) => {
    const [selectedAnswer, setSelectedAnswer] = useState(props.selectedAnswer);

    const answerSelected = (choice: OptionValueLabel) => {
        setSelectedAnswer(choice);
        props.onAnswerUpdate(choice);
    }

    const isOptional = () => props.optional === true; //default to false if not provided

    useEffect(() => {
        // Fixes a bug on safari where dropdown unpopulates
        if (props.selectedAnswer) {
            setSelectedAnswer(props.selectedAnswer);
        }
    }, [props.selectedAnswer]);

    return (
        <div className={`main-form-selector`}>
            <UncontrolledDropdown>
                <DropdownToggle
                    className={`form-control${props.invalid && !isOptional() ? ' is-invalid' : ''}${!selectedAnswer ? ' is-faded' : ''}`}
                    tag="div">
                    <div className="de-select-options">
                        {selectedAnswer ? selectedAnswer.label : <span className="de-select-default">Select</span>}
                    </div>
                    <div className="de-select-button">
                        <ExpandMore className="expanded-show" />
                        <ExpandLess className="expanded-hide" />
                    </div>
                </DropdownToggle>
                <DropdownMenu>
                    {props.options.map((choice) => (
                        <DropdownItem
                            key={choice.value}
                            className={`form-check-inline dropdown-item ${choice.value}`}
                            onClick={() => answerSelected(choice)}>
                            <Label className={`form-check-label`}>{choice.label}</Label>
                        </DropdownItem>
                    ))}
                </DropdownMenu>
            </UncontrolledDropdown>
            <FormFeedback className={`${props.invalid && !isOptional() ? "d-block" : ""}`}>Required</FormFeedback>
        </div>
    );
}

const getPositionTitleOptions = () => {
    let o = [
        { value: 1, label: "Frontline" },
        { value: 2, label: "Superintendent" },
        { value: 3, label: "Manager" },
        { value: 4, label: "Supervisor" }
    ] as OptionValueLabel[];
    return o;
}

export const positionTitleOptions = getPositionTitleOptions();

export const PositionLevelChoices = (props: AnswerProps) => {
    return (
        <DropdownSelect {...props} options={positionTitleOptions} />
    );
}

const getVehicleOwnerOptions = () => {
    let o = [
        { value: 1, label: "BHP-Owned" },
        { value: 2, label: "Hired Light Vehicle" },
        { value: 3, label: "Contract Partner" }
    ] as OptionValueLabel[];
    return o;
}

export const vehicleOwnerOptions = getVehicleOwnerOptions();

export const VehicleOwnerChoices = (props: AnswerProps) => {
    return (
        <DropdownSelect {...props} options={vehicleOwnerOptions} />
    );
}

const getHoursOfDrivingOptions = () => {
    let o = [
        { value: 1, label: "Daylight Hours Only" },
        { value: 2, label: "Night Driving Required" }
    ] as OptionValueLabel[];
    return o;
}

export const hoursOfDrivingOptions = getHoursOfDrivingOptions();

export const HoursOfDrivingChoices = (props: AnswerProps) => {
    return (
        <DropdownSelect {...props} options={hoursOfDrivingOptions} />
    );
}

const getLVTravelShiftOptions = () => {
    let o = [
        { value: 1, label: "Dayshift Travel Only" },
        { value: 2, label: "Dayshift & Nightshift Travel" }
    ] as OptionValueLabel[];
    return o;
}

export const lvTravelShifOptions = getLVTravelShiftOptions();

export const LvTravelShifChoices = (props: AnswerProps) => {
    return (
        <DropdownSelect {...props} options={lvTravelShifOptions} />
    );
}

export const DepartmentManagerChoices = (props: AnswerProps) => {
    const [options, setOptions] = useState([] as OptionValueLabel[]);

    useEffect(() => {
        let mounted = true;
        const mount = async () => {
            const results = await getAllDepartmentManagers();
            if (mounted) {
                let departmentManagers = results.map(manager => ({ value: manager.id, label: `${manager.firstName} ${manager.lastName }` }));
                setOptions(departmentManagers);
            }
        };

        mount();
        return () => { mounted = false }
    }, []);

    return (
        <DropdownSelect {...props} options={options} />
    );
}

interface OnBehalfProp {
    id?: number;
}

export const RPUserReportManager = (props: OnBehalfProp) => {
    const [options, setOptions] = useState({} as UserSupervisor);
    useEffect(() => {
        let mounted = true;
        const mount = async () => {
            let result;
            if (!props.id) { result = await getUserReportToManager() } else {

                result = await getUserOnbehalfReportToManager(props.id)
            }
            if (mounted) {
                if (result) {
                    let reportToManager = { email: result.email, name: result.name };
                    setOptions(reportToManager);
                    usingRPAutoPopulatedReportToEmail();
                }                
            }
        };

        mount();
        return () => { mounted = false }
    }, [props.id]);

    return (
        <>
            {options?.name && options?.email ? `${options.name} (${options.email})` : '1 - UP email not available. Please manually enter below' }
        </>
    );
}

interface TextInputProps extends AnswerProps {
    placeholder?: string;
    emailValidation?: boolean;
    onMouseOutUpdate?: (value: any) => void;
    emailExist?: boolean;
}

export const SingleLineTextInput = (props: TextInputProps) => {
    const [text, setText] = useState('');
    const maxLength = 100;

    const getValue = (event: any) => {
        return (event?.target?.value || '').substring(0, maxLength) as string;
    }

    const answerUpdate = (event: any) => {
        let value = getValue(event);
        props.onAnswerUpdate(value);
        setText(value);

        if (typeof props.onMouseOutUpdate === 'function') {
            if (value !== "" && props.emailExist) {
                props.onMouseOutUpdate!(value);
            }

            if (value === "" && !props.emailValidation) {
                props.onMouseOutUpdate!(value);
            }
        }        
    }

    const mouseOutUpdate = (event: any) => {
        let value = getValue(event);
        if (typeof props.onMouseOutUpdate === 'function' && value !== "" && !props.emailExist) {
            props.onMouseOutUpdate!(value);
        }        
    }

    return (
        <>
            <Input
                type="text"
                value={text}
                placeholder={props.placeholder || `Enter text here (max. ${maxLength} characters)`}
                maxLength={maxLength}
                onChange={answerUpdate}
                required={true}
                invalid={props.emailValidation || props.invalid}
                onMouseOut={mouseOutUpdate}
            />
            <FormFeedback>Required</FormFeedback>
        </>
    )
}

interface DateChoiceProps extends AnswerProps {
    date?: Date
}

export const DateChoice = (props: DateChoiceProps) => {
    const dayRange = 364;
    const currentDate = new Date();

    function addDays(date: Date, days: number) {
        let result = new Date(date);
        result.setDate(result.getDate() + days);
        return result;
    }

    const minDate = currentDate;
    const maxDate = addDays(currentDate, dayRange * 1);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        props.onAnswerUpdate(e);
    }

    useEffect(() => {
        props.onAnswerUpdate(toISOLocalDate(props.date || currentDate));
    }, [])

    return (
        <div className="main-form-selector">
            <Input
                type="date"
                id="expiryDate"
                min={toISOLocalDate(minDate)}
                max={toISOLocalDate(maxDate)}
                defaultValue={toISOLocalDate(props.date || currentDate)}
                onChange={onChange}
            />
        </div>
    );
}

interface LocationChoiceProps extends AnswerProps {
    defaultLocationId?: number;
}

export const OtherLocation = { value: -2, label: "Other" };

export const LocationChoice = (props: LocationChoiceProps) => {
    const [options, setOptions] = useState([] as OptionValueLabel[]);
    const [selectedAnswer, setSelectedAnswer] = useState(undefined as undefined | OptionValueLabel);

    // const answerUpdate = (event: any) => {
    //     let value = getValue(event);
    //     props.onAnswerUpdate(value);
    //     setLocationId(value);
    // }

    useEffect(() => {
        let mounted = true;
        const mount = async () => {
            const results = await getLocations();
            if (mounted) {
                let locations = results.map(location => ({ value: location.id, label: `${location.name}` }));
                locations.push(OtherLocation);
                setOptions(locations);
            }
        };


        mount();
        return () => { mounted = false };
    }, []);

    return (
        <DropdownSelect {...props} options={options} selectedAnswer={selectedAnswer} />
    );
}

const getTaskOptions = () => {
    let o = [
        { value: 1, label: "Yes", color: 'green' },
        { value: 2, label: "No", color: 'red' },
        { value: 3, label: "N/A", color: 'black' }
    ] as OptionValueLabel[];
    return o;
}
export const yesNoNaOptions = getTaskOptions();

interface ITaskChoicesProps extends AnswerProps {
    name: string;
    section?: number;
}

export const TaskChoices = (props: ITaskChoicesProps) => {
    const [selectedValue, setSelectedValue] = useState<number | undefined>();

    const handleChange = (e: any) => {
        setSelectedValue(Number(e.target.value));
        props.onAnswerUpdate(e);
    };

    const getClassName = (value: number) => {
        let radio = '';

        if (selectedValue === value) {            
            if (value === 1 && props.section === 1) {
                radio = 'radio-green';
            } else if (value === 2 && props.section === 1) {
                radio = 'radio-red';
            }

            if (value === 1 && props.section === 2) {
                radio = 'radio-red';
            } else if (value === 2 && props.section === 2) {
                radio = 'radio-green';
            }
        }
        return radio;
    };

    return (
        <>
            <div className="d-flex justify-content-between w-100">
                {yesNoNaOptions.map((option) => (
                    <div key={`${props.name}_${option.value}`} className="answer-select">
                        <Form.Check required type="radio" id={`${props.name}_${option.value}`} name={props.name} value={option.value} onChange={handleChange} className={getClassName(option.value)} label={option.label} />
                    </div>
                ))}
            </div>
            <FormFeedback className={`${props.invalid ? "d-block" : ""}`}>Required</FormFeedback>
        </>
    );
}

export const OtherHazard = { value: -2, label: "Other" };

export const HazardChoice = (props: any) => {
    const OtherHazard = { value: -2, label: "Other", questionNumber: '0.0' };
    const [options, setOptions] = useState([] as any[]);
    const [filteredOptions, setFilteredOptions] = useState([] as any[]);
    const [selectedAnswer, setSelectedAnswer] = useState(undefined as undefined | OptionValueLabel);
    const q = TakeFiveQuestions;
    const lastHazardRef = React.useRef<number | undefined>();

    useEffect(() => {
        let mounted = true;
        const mount = async () => {
            const results = await getHazards();
            let hazards = results.map(hazard => ({ value: hazard.id, label: `${hazard.hazard}`, questionNumber: hazard.hazardNumber }));
            hazards.push(OtherHazard);
            setOptions(hazards);
        };

        mount();
        return () => { mounted = false };
    }, []);

    useEffect(() => {
        const sub = t.subscribe((s: TakeFiveFormState) => {
            if (options.length > 0) {
                const ids: number[] = [];
                Object.entries(s.form).forEach(([key, value]) => {
                    const question = q[key as keyof typeof q];
                    if (question && (question as any).id !== undefined
                        && (question as any).section !== undefined
                        && (question as any).section === 1 && value === '2') {
                        ids.push((question as any).id);
                    }

                    if (question && (question as any).id !== undefined
                        && (question as any).section !== undefined
                        && (question as any).section === 2 && value === '1') {
                        ids.push((question as any).id);
                    }
                });
                let filtered = options.filter(option => ids.indexOf(Number(option.questionNumber)) !== -1);
                setFilteredOptions(filtered);

                // Update selected answer only if hazard value changed
                if (s.form.hazard !== lastHazardRef.current) {
                    lastHazardRef.current = s.form.hazard;
                    if (s.form.hazard) {
                        const selected = options.find(opt => opt.value === s.form.hazard);
                        if (selected) {
                            setSelectedAnswer(selected);
                        }
                    } else {
                        setSelectedAnswer(undefined);
                    }
                }
            }
        });

        return () => { sub.unsubscribe(); };
    }, [options]);

    return (
        <DropdownSelect {...props} options={filteredOptions} selectedAnswer={selectedAnswer} />
    );
}

export const HazardRiskChoice = (props: any) => {
    const [options, setOptions] = useState([] as any[]);
    const [filteredOptions, setFilteredOptions] = useState([] as any[]);
    const [selectedAnswer, setSelectedAnswer] = useState(undefined as undefined | OptionValueLabel);

    useEffect(() => {
        let mounted = true;
        const mount = async () => {
            const results = await getRisks();
            let risks = results.map(hazardRisk => ({ value: hazardRisk.id, label: `${hazardRisk.risk}`, type: hazardRisk.type }));
            if (mounted) {
                setOptions(risks);
            }
        };

        mount();
        return () => { mounted = false };
    }, []);

    useEffect(() => {
        if (props.riskType && options.length > 0) {
            let newFilteredOptions = options.filter(option => option.type === +props.riskType);
            setFilteredOptions(newFilteredOptions);
        }
    }, [props.riskType, options]);

    // Update selected answer based on prop
    useEffect(() => {
        if (props.selectedRiskId && filteredOptions.length > 0) {
            const selected = filteredOptions.find(opt => opt.value === props.selectedRiskId);
            if (selected) {
                setSelectedAnswer(selected);
                return;
            }
        }
        if (props.selectedRiskId && options.length > 0) {
            const selected = options.find(opt => opt.value === props.selectedRiskId);
            if (selected) {
                setSelectedAnswer(selected);
                return;
            }
        }
        if (!props.selectedRiskId) {
            setSelectedAnswer(undefined);
        }
    }, [props.selectedRiskId, filteredOptions, options]);

    return (
        <DropdownSelect {...props} options={filteredOptions} selectedAnswer={selectedAnswer} />
    );
}

const getRiskTypeOptions = () => {
    let o = [
        { value: 1, label: "Material" },
        { value: 2, label: "Non Material" }
    ] as OptionValueLabel[];
    return o;
}

export const riskTypeOptions = getRiskTypeOptions();

export const RiskTypeChoices = (props: any) => {
    const [userChoice, setUserChoice] = useState<number | undefined>();
    const lastRiskTypeRef = React.useRef<number | undefined>();

    useEffect(() => {
        const sub = t.subscribe((s: TakeFiveFormState) => {
            // Update selected answer only if riskType value changed
            if (s.form.riskType !== lastRiskTypeRef.current) {
                lastRiskTypeRef.current = s.form.riskType ? Number(s.form.riskType) : undefined;
                setUserChoice(lastRiskTypeRef.current);
            }
        });

        return () => { sub.unsubscribe(); };
    }, []);

    return (
        <>
            {riskTypeOptions.map((option) => (
                <div key={`${props.name}_${option.value}`} className="answer-select row pt-1 pb-1">
                    <div className="col-1">
                        <Form.Check 
                            required 
                            type="radio" 
                            id={`${props.name}_${option.value}`} 
                            name={props.name} 
                            value={option.value} 
                            onChange={props.onAnswerUpdate}
                            checked={userChoice === option.value}
                        />
                    </div>
                    <div className="col pl-1">
                        <label htmlFor={`${props.name}_${option.value}`}>{option.label}</label><br />
                    </div>
                </div>
            ))}
            <FormFeedback className={`${props.invalid ? "d-block" : ""}`}>Required</FormFeedback>
        </>
    );
}

export const MultilineTextInput = (props: TextInputProps) => {
    const [text, setText] = useState('');
    const maxLength = 500;
    const lastControlRef = React.useRef<string | undefined>();

    useEffect(() => {
        const sub = t.subscribe((s: TakeFiveFormState) => {
            // Update text only if control value changed
            if (s.form.control !== lastControlRef.current) {
                lastControlRef.current = s.form.control;
                setText(s.form.control || '');
            }
        });

        return () => { sub.unsubscribe(); };
    }, []);

    const answerUpdate = (event: any) => {
        let value = (event?.target?.value || '').substring(0, maxLength) as string;
        props.onAnswerUpdate(value);
        setText(value);
        lastControlRef.current = value;
    }

    return (
        <>
            <Input
                type="textarea"
                value={text}
                className="multiline-input"
                placeholder={props.placeholder || `Enter text here (max. ${maxLength} characters)`}
                maxLength={maxLength}
                rows={5}
                onChange={answerUpdate}
                required={true}
                invalid={props.invalid}
            />
            <FormFeedback>Required</FormFeedback>
        </>
    )
}